import React, { useEffect } from "react";
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';
import { Ride } from "./_models";
import { ConditionalShow } from "../builders/FormBuilder";
import Html from 'react-pdf-html';

Font.register({
    family: 'Ubuntu',
    fonts: [
        {
            src: 'https://fonts.gstatic.com/s/questrial/v13/QdVUSTchPBm7nuUeVf7EuStkm20oJA.ttf',
        },
        {
            src: 'https://fonts.gstatic.com/s/questrial/v13/QdVUSTchPBm7nuUeVf7EuStkm20oJA.ttf',
            fontWeight: 'bold',
        },
        {
            src: 'https://fonts.gstatic.com/s/questrial/v13/QdVUSTchPBm7nuUeVf7EuStkm20oJA.ttf',
            fontWeight: 'normal',
            fontStyle: 'italic',
        },
    ],
});

type ExportPDFButtonProps<T,> = {
    Document: React.FunctionComponent<{ entity: T }>
    entity: any
    entityFill?: (entity: T) => Promise<T>
} & ConditionalShow<T>


function isPromise(p: any): boolean {
    if (typeof p === 'object' && typeof p.then === 'function') {
        return true;
    }

    return false;
}


const getParsedDriverNote = (entity: Ride): string => `
    <html>
    <body>
        <style>
        * {
            line-height: 1.5;
            font-family: Ubuntu;
            text-align: left;
            background-color: white;
        }
        p, i, div, a, span, h1, h2, h3, h4, h5, h6 {
            font-size: 10px !important;
            margin:0;
        }
        </style>
        <div style=" font-size: 0.55px !important; ">
            ${(entity?.notes_for_driver ?? "")}
        </div>
    </body>
    </html>
`

const getParsedItineraryDescription = (entity: Ride): string => `
    <html>
    <body>
        <style>
        * {
            line-height: 1.5;
            font-family: Ubuntu;
        }
        p, i, div, a, span, h1, h2, h3, h4, h5, h6 {
            font-size: 10px !important;
            margin:0;
        }
        </style>
        <div style=" font-size: 0.55px !important; ">
            ${(entity?.ride_note ?? "")}
        </div>
    </body>
    </html>
`;

//TODO: move to a separate generic file
export const ExportPDFButton = <T,>({ entity: entityPassed, Document, entityFill, showIf }: ExportPDFButtonProps<T>) => {
    const [show, setShow] = React.useState(false);
    const [entity, setEntity] = React.useState(entityPassed);
    const [generationRequested, setGenerationRequested] = React.useState(false);


    useEffect(() => {
        if (!showIf) {
            return setShow(true)
        }
        showIf(entity)
            .then((res: boolean) => setShow(res))
    }, [entity, showIf])

    const resolveEntity = async () => {
        if (isPromise(entity)) {
            setEntity(await entityPassed);
        }
        if (entityFill) {
            setEntity(await entityFill(entityPassed));
        }
    }

    React.useEffect(() => {
        setGenerationRequested(false);
    }, [entityPassed, entityFill])

    React.useEffect(() => {
        resolveEntity();
    }, [generationRequested])

    const entityID = (entity as any)?.id;
    let entityIdx = entity?.service?.rides?.findIndex((r: any) => r.id == entityID);
    if (typeof entityIdx == 'number') {
        entity.idx = entityIdx + 1;
    }

    return (
        show ? (generationRequested ?
            <>
                <PDFDownloadLink document={<Document entity={entity} />} fileName={entityID ? `${entityID}.pdf` : "documento.pdf"}>
                    {({ blob, url, loading, error }) =>
                        (loading || error) ?
                            'Sto generando il documento...' :
                            <button
                                type="button"
                                onClick={(e) => url && window.open(url, '_blank')}
                                className="btn tp-btn-primary me-3 ">
                                {`Scarica come PDF`}
                            </button>
                    }
                </PDFDownloadLink>
            </>
            :
            <button
                type="button"
                onClick={() => setGenerationRequested(true)}
                className="btn tp-btn-primary">
                {`Genera PDF`}
            </button>
        ) : <></>
    );
}
// Create styles
const styles = StyleSheet.create({
    "*": {
        fontFamily: 'Ubuntu',
        textAlign: "left",
        backgroundColor: "white",
        whiteSpace: "nowrap",
    },
    page: {
        fontFamily: 'Ubuntu',
        textAlign: "left",
        backgroundColor: "white",
        whiteSpace: "nowrap",
        padding: "60 35",
    },
    section: {
        margin: 5,
        padding: 2,
        textAlign: "left"
    },
    subSection: {
        margin: 1,
        padding: 1,
    },
    field: {
        flexDirection: "row",
        alignItems: "baseline",
        marginTop: 3,
        marginBottom: 3,
    },
    fieldLeft: {
        marginTop: 3,
        marginBottom: 3,
        textAlign: "left",
        flexDirection: "row",
        alignItems: "flex-start",
        justifyContent: "flex-start"
    },
    fieldCenter: {
        marginTop: 3,
        marginBottom: 3,
        textAlign: "center",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center"
    },
    appTitle: {
        fontSize: 16,
        marginTop: 5,
        marginBottom: 15,
        textTransform: "uppercase",
        fontWeight: 900,
        textAlign: "left",
    },
    title: {
        fontSize: 16,
        marginTop: 5,
        marginBottom: 5,
        textTransform: "uppercase",
        textAlign: "left",
    },
    subTitle: {
        textTransform: "uppercase",
        fontSize: 13,
        fontWeight: "bold",
        marginTop: 10,
        marginBottom: 3,
        color: "#043C50",
    },
    label: {
        fontSize: 11,
        marginRight: 5,
        color: "#043C50",
    },
    text: {
        fontSize: 11,
    },
});

type RideDocumentProps = {
    entity: Ride
}

// Create Document Component
export const RideDocument = ({ entity }: RideDocumentProps) => {
    return <Document>
        <Page size="A4" style={styles.page}>
            <View style={styles.section}>
                <Text style={styles.appTitle}>Transfer Planner</Text>
                <View style={styles.fieldLeft}>
                    <Text style={styles.label}>Titolo del Servizio:</Text>
                    <Text style={styles.text}>{entity?.service?.title}</Text>
                </View>
                <View style={styles.fieldLeft}>
                    <Text style={styles.label}>CORSA {entity?.idx}</Text>
                </View>
                <View style={styles.fieldLeft}>
                    <Text style={styles.label}>Titolo della Corsa:</Text>
                    <Text style={styles.text}>{entity?.itinerary}</Text>
                </View>
                <View style={styles.fieldLeft}>
                    <View style={styles.fieldLeft}>
                        <Text style={styles.label}>Mezzo:</Text>
                        <Text style={styles.text}>{entity?.mean?.code ?? ""}{entity?.mean?.license_plate ?? ""}&nbsp;-&nbsp;</Text>
                    </View>
                    <View style={styles.fieldLeft}>
                        <Text style={styles.label}>Autista:</Text>
                        <Text style={styles.text}>{entity?.driver?.name ?? ""} {entity?.driver?.surname ?? ""}</Text>
                    </View>
                </View>
            </View>

            <View style={styles.section}>
                <Text style={styles.subTitle}>Partenza</Text>
                <View style={styles.field}>
                    <View style={styles.field}>
                        <Text style={styles.label}>Data e ora di ritrovo:</Text>
                        <Text style={styles.text}>{entity?.start_appointment_date}&nbsp;&nbsp;</Text>
                    </View>
                    <View style={styles.field}>
                        <Text style={styles.label}>Data e ora di partenza:</Text>
                        <Text style={styles.text}>{entity?.start_departure_date}</Text>
                    </View>
                </View>
                <View style={styles.field}>
                    <Text style={styles.label}>Luogo di partenza:</Text>
                    <Text style={styles.text}>{entity?.start_place}</Text>
                </View>
            </View>

            <View style={styles.section}>
                <Text style={styles.subTitle}>Destinazione</Text>
                <View style={styles.field}>
                    <Text style={styles.label}>Luogo di destinazione:</Text>
                    <Text style={styles.text}>{entity?.end_place}</Text>
                </View>
            </View>

            <View style={{ ...styles.section, marginTop: 15 }}>
                <View style={styles.field}>
                    <Text style={styles.label}>Data e ora di fine corsa:</Text>
                    <Text style={styles.text}>{entity?.end_departure_date}</Text>
                </View>
            </View>

            <View style={styles.section}>
                <View style={styles.field}>
                    <Text style={styles.label}>Descrizione itinerario</Text>
                </View>
                <View style={styles.field}>
                    <Html resetStyles>
                        {getParsedItineraryDescription(entity)}
                    </Html>
                </View>
            </View>

            <View style={styles.section}>
                <View style={styles.field}>
                    <Text style={styles.label}>Passeggeri:</Text>
                    <Text style={styles.text}>{entity?.passengers}</Text>
                </View>
                <View style={styles.field}>
                    <View style={styles.field}>
                        <Text style={styles.label}>Referente cliente:</Text>
                        <Text style={styles.text}>{entity?.service?.client_referent}&nbsp;&nbsp;</Text>
                    </View>
                    <View style={styles.field}>
                        <Text style={styles.label}>Tel:</Text>
                        <Text style={styles.text}>{entity?.service?.client_referent_phone}</Text>
                    </View>
                </View>
            </View>

            <View style={styles.section}>
                <View style={styles.field}>
                    <Text style={styles.label}>Note per l'autista</Text>
                </View>
                <View>
                    <Html resetStyles>
                        {getParsedDriverNote(entity)}
                    </Html>
                </View>
            </View>
        </Page>
    </Document>
};
